import { Control, Controller, FieldValues, FieldPath } from 'react-hook-form';
import { cn } from '@/lib/utils.ts';
import InputLabel from '@/components/form/base/InputLabel.tsx';
import InputHint from '@/components/form/base/InputHint.tsx';
import InputError from '@/components/form/base/InputError.tsx';
import PrimitiveSingleSelect from '@/components/form/_primitives/PrimitiveSingleSelect.tsx';
import Option from '@/components/form/types/Option.ts';
import { Props as SelectProps } from 'react-select';
import { SelectControlSize } from '@/components/form/_primitives/primitiveSelectStyles.ts';

type SingleSelectInputProps<T extends FieldValues> = {
  fieldName: FieldPath<T>;
  control: Control<T>;
  options: Option[];
  label?: string;
  required?: boolean;
  hint?: string;
  error?: string;
  className?: string;
  onChange?: (value: string | object | undefined) => void;
  /** 30 px zamiast 34 px — wyłącznie dla kontrolek paska narzędzi tabeli. */
  controlSize?: SelectControlSize;
} & Omit<
  SelectProps<Option, false>,
  'value' | 'onChange' | 'isMulti' | 'name' | 'inputId' | 'options'
>;

const SingleSelectInput = <T extends FieldValues>({
  fieldName,
  control,
  options,
  label,
  required = false,
  hint,
  error,
  className,
  onChange,
  ...props
}: SingleSelectInputProps<T>) => {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      {label && (
        <InputLabel
          label={label}
          required={required}
          disabled={props.isDisabled}
        />
      )}
      <Controller
        control={control}
        name={fieldName}
        render={({ field, fieldState }) => {
          const errorMessage = error ?? fieldState.error?.message;
          return (
            <>
              <PrimitiveSingleSelect
                {...props}
                mobileMenuTitle={props.mobileMenuTitle ?? label}
                name={fieldName}
                options={options}
                id={fieldName}
                error={errorMessage}
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                  onChange?.(value);
                }}
              />
              <InputError error={errorMessage} />
            </>
          );
        }}
      />
      {hint && <InputHint hint={hint} />}
    </div>
  );
};

export default SingleSelectInput;
